import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { type VariantProps, tv } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";
import Transition from "../../atoms/animations/Transition";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import type { IconName } from "../icon/names";

const ToastVariant = tv({
  slots: {
    wrapper: "fixed bottom-0 end-0 z-[21474830019] m-5 pointer-events-auto",
    base: "body-md flex min-h-[36px] w-72 shrink-0 flex-col overflow-hidden rounded-xl shadow-lg",
    content: "body-md flex shrink-0 gap-2 p-3 leading-tight",
    icon: "",
    close: "cursor-pointer text-white opacity-50 hover:opacity-100 p-0 w-5 h-5",
  },
  variants: {
    isShowing: {
      false: {
        wrapper: "pointer-events-none",
      },
    },
    variant: {
      default: {
        base: "bg-text-default text-white",
        close: "hover:bg-transparent hover:text-white active:bg-transparent",
      },
      success: {
        base: "bg-border-success-default text-white",
        close: "hover:bg-white hover:text-text-success active:bg-white",
      },
      critical: {
        base: "bg-border-critical-default text-white",
        close: "hover:bg-white hover:text-text-critical active:bg-white",
      },
      white: {
        base: "bg-surface-default text-text-default border border-border-subdued",
        close:
          "text-text-default hover:bg-text-default hover:text-white active:bg-text-default",
      },
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface ToastProps
  extends VariantProps<typeof ToastVariant>,
    React.HTMLAttributes<HTMLDivElement> {
  i?: number;
  show?: boolean;
  leadingIcon?: IconName;
  leadingIconClassName?: string;
  leadingIconProps?: React.ComponentProps<typeof Icon>;
  trailingIcon?: string;
  trailingIconClassName?: string;
  trailingIconProps?: React.ComponentProps<typeof Icon>;
  dataCy?: string;
  children: React.ReactNode;
  variant?: "success" | "critical" | "white";
  autoHide?: boolean;
  hide?: boolean;
  handleClose?: () => void;
}

const Toast = ({
  i,
  children,
  leadingIcon,
  leadingIconClassName,
  leadingIconProps,
  variant,
  className,
  hide,
  show = true,
  autoHide = true,
  handleClose,
  ...props
}: ToastProps) => {
  const [isHover, setIsHover] = useState(false);
  const [isShowing, setIsShowing] = useState(show);
  const { wrapper, base, content, icon, close } = ToastVariant({
    variant,
    isShowing: !!isShowing,
  });
  const timeoutId = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (show) {
      setIsShowing(true);
    } else if (autoHide) {
      // ensure that the toast is shown for at least 5 seconds
      timeoutId.current = setTimeout(() => setIsShowing(false), 5000);
    }

    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [autoHide, show]);
  useEffect(() => {
    if (hide) {
      clearTimeout(timeoutId.current);
      setIsShowing(false);
    }
  }, [hide]);
  const message = useRef<string | null>(
    typeof children === "string" ? children : null,
  );
  useEffect(() => {
    if (typeof children === "string" && message.current !== children) {
      message.current = children;
    }
  }, [children]);

  const [intercomPresent, setIntercomPresent] = useState(false);
  useEffect(() => {
    const intercomFrame = document.getElementById("intercom-frame");
    setIntercomPresent(!!intercomFrame);
  }, []);

  // implement radix
  return ReactDOM.createPortal(
    <div
      className={cn(
        wrapper({ className }),
        intercomPresent && "-translate-x-16",
      )}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <Transition
        from={{ opacity: 0, x: 0, y: 100 }}
        enter={{ opacity: 1, x: 0, y: 0 + (i || 0) * 100 }}
        leave={{ opacity: 0, x: 0, y: 100 }}
        className="!relative"
        show={isShowing || isHover}
      >
        <div className={cn(base())} {...props}>
          <div className={cn(content())}>
            {leadingIcon && (
              <Icon
                name={leadingIcon}
                className={cn("shrink-0", icon(), leadingIconClassName)}
                {...leadingIconProps}
              />
            )}
            {children}
            <Button
              variant="shapeless"
              classNameWrapper="ms-auto mt-[0.75px] shrink-0"
              className={cn(close())}
              leadingIcon="x-md"
              onClick={() => {
                setIsShowing(false);
                setIsHover(false);
                handleClose?.();
              }}
            />
          </div>
        </div>
      </Transition>
    </div>,
    document.body,
  );
};
export default Toast;
